import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiClient } from '../../lib/api';
import { AuthLayout } from '../../components/layouts/AuthLayout';
import AuthFloatingInput from '../../components/auth/AuthFloatingInput';
import { slideUp } from '@/lib/motion';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateEmail = (email: string): boolean => {
    if (!email) {
      setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      return;
    }

    setLoading(true);

    try {
      await apiClient.post('/auth/forgot-password', { email });
      // Always show success to prevent email enumeration
      // Navigate to code entry page with email in state
      navigate('/auth/enter-code', { state: { email } });
    } catch (err: any) {
      const status = err.response?.status;
      const data = err.response?.data;
      const apiMessage = data?.message || data?.error;

      if (status === 429) {
        setError(apiMessage || 'Too many requests. Please try again later.');
      } else if (status === 400) {
        const fieldErrors = data?.errors as Record<string, string[]> | undefined;
        setError(fieldErrors?.email?.[0] || apiMessage || 'Please enter a valid email address.');
      } else {
        // For other errors (404, 500, etc.), still navigate to prevent email enumeration
        navigate('/auth/enter-code', { state: { email } });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <motion.div {...slideUp}>
      {/* Icon */}
      <div className="flex justify-center mb-6">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center">
          <svg
            width="96"
            height="96"
            viewBox="0 0 96 96"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M36.3238 29.3905V37.1505C36.3238 39.8031 34.1734 41.9535 31.5208 41.9535C28.8682 41.9535 26.7178 39.8031 26.7178 37.1505V29.3905C26.7178 17.7136 36.1226 8.19195 47.7552 8.00268L48.1085 8C59.7856 8 69.3069 17.4049 69.4962 29.0373L69.499 29.3905V37.1505C69.499 39.8031 67.3487 41.9535 64.6961 41.9535C62.0434 41.9535 59.8931 39.8031 59.8931 37.1505V29.3905C59.8931 22.9575 54.7117 17.7118 48.3031 17.6075L48.1085 17.6059C41.6103 17.6059 36.3238 22.8925 36.3238 29.3905Z"
              className="fill-primary-200"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M79.2169 68.1275C79.2169 79.1055 70.3177 88.0047 59.3399 88.0047H36.8771C25.8992 88.0047 17 79.1055 17 68.1275V47.8769C17 43.3057 20.7056 39.6001 25.2769 39.6001H48.1075L48.1085 39.6007L70.94 39.6001C75.5113 39.6001 79.2169 43.3057 79.2169 47.8769V68.1275ZM44.5109 66.8621C44.5109 68.8492 46.1218 70.4601 48.1089 70.4601H49.5008C50.7192 70.4601 51.7069 69.4724 51.7069 68.254C51.7069 67.3451 52.2769 66.5489 53.0652 66.0965C53.8915 65.6222 54.6536 65.029 55.3191 64.3292C57.2141 62.3285 58.1785 59.7142 58.0344 56.9604C57.7658 51.7841 53.4962 47.6487 48.3104 47.5434C48.289 47.543 48.2677 47.543 48.2463 47.543L48.112 47.5445C48.1112 47.5445 48.1104 47.5443 48.1097 47.5439C48.109 47.5436 48.1083 47.5434 48.1075 47.5434C43.4654 47.544 38.9555 51.1224 38.26 56.1016C38.189 56.6459 38.6269 57.1041 39.1759 57.1041H45.3796C45.3847 57.1041 45.3889 57.0999 45.3889 57.0948C45.4151 56.9074 45.4596 56.7267 45.5206 56.5545L45.5412 56.4982C45.5512 56.4718 45.5616 56.4456 45.5724 56.4196L45.5999 56.3557C45.664 56.2116 45.74 56.0745 45.8267 55.9454C45.9407 55.7756 46.0732 55.6198 46.2213 55.4806C46.3192 55.3886 46.4239 55.3037 46.5346 55.227L46.6047 55.1799L46.6652 55.1418L46.7087 55.1157L46.7571 55.088C46.7824 55.0739 46.8079 55.0601 46.8338 55.0467C46.8792 55.0231 46.9253 55.0009 46.9722 54.9798L47.059 54.9427C47.1037 54.9244 47.1491 54.9073 47.195 54.8914L47.2544 54.8715C47.3302 54.8471 47.4076 54.8259 47.4862 54.808C47.5258 54.799 47.5658 54.7908 47.606 54.7835L47.6634 54.7737L47.7212 54.7651L47.7882 54.7567C47.8304 54.7519 47.8729 54.748 47.9156 54.7451L47.9774 54.7416L48.0395 54.7394L48.1076 54.7386C48.1082 54.7386 48.1087 54.7391 48.1087 54.7397C48.1087 54.7404 48.1092 54.7409 48.1098 54.7409L48.1665 54.7392C49.572 54.7681 50.7762 55.929 50.8481 57.3347C50.8865 58.0973 50.6226 58.8217 50.0997 59.3736L50.0514 59.4228L49.9918 59.4805C49.4961 59.9469 48.8633 60.2083 48.1845 60.2264L48.1095 60.2274L47.4332 60.2272C45.8192 60.2272 44.5109 61.5356 44.5109 63.1496V66.8621ZM48.1087 72.9748C48.1087 72.975 48.1088 72.975 48.1087 72.9748C46.151 72.9749 44.5635 74.5615 44.5635 76.519C44.5635 78.4763 46.1503 80.0633 48.1078 80.0634C48.1078 80.0632 48.1079 80.0633 48.1078 80.0634L48.1089 80.0632L48.1724 80.0626C50.1001 80.0283 51.6521 78.4549 51.6521 76.5188C51.6521 74.5617 50.0657 72.9753 48.1087 72.9748Z"
              className="fill-primary-500"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M48.1089 70.4457C46.1218 70.4457 44.5109 68.8348 44.5109 66.8477V63.1352C44.5109 61.5212 45.8192 60.2128 47.4332 60.2128H48.1087C48.1088 60.2128 48.1089 60.2129 48.1091 60.2129C48.1092 60.213 48.1093 60.213 48.1095 60.213L48.1845 60.212C48.8633 60.1939 49.4961 59.9325 49.9918 59.4661L50.0514 59.4084L50.0997 59.3592C50.6226 58.8073 50.8865 58.0829 50.8481 57.3203C50.7762 55.9146 49.572 54.7537 48.1665 54.7248L48.1098 54.7265C48.1092 54.7265 48.1087 54.726 48.1087 54.7253C48.1087 54.7247 48.1082 54.7242 48.1076 54.7242L48.0395 54.725L47.9774 54.7272L47.9156 54.7307C47.8729 54.7336 47.8304 54.7374 47.7882 54.7422L47.7212 54.7507L47.6634 54.7593L47.606 54.7691C47.5658 54.7764 47.5258 54.7846 47.4862 54.7936C47.4075 54.8115 47.3302 54.8327 47.2544 54.8571L47.195 54.877C47.1491 54.8929 47.1037 54.91 47.059 54.9283L46.9715 54.9657L46.9923 54.9565C46.9386 54.9801 46.8857 55.0054 46.8338 55.0323C46.8079 55.0457 46.7824 55.0594 46.7571 55.0736L46.7087 55.1013L46.6652 55.1274L46.6047 55.1655L46.5346 55.2126C46.4239 55.2893 46.3192 55.3742 46.2213 55.4662C46.0732 55.6054 45.9407 55.7612 45.8267 55.931C45.74 56.0601 45.664 56.1972 45.5999 56.3413L45.5724 56.4052C45.5616 56.4312 45.5512 56.4574 45.5412 56.4838L45.5206 56.5401C45.4596 56.7123 45.4151 56.893 45.3889 57.0804C45.3889 57.0855 45.3847 57.0897 45.3796 57.0897H39.1759C38.6269 57.0897 38.189 56.6315 38.26 56.0872C38.9555 51.108 43.4654 47.5296 48.1075 47.529C48.1083 47.529 48.109 47.5292 48.1097 47.5295C48.1104 47.5299 48.1112 47.5301 48.112 47.5301L48.2463 47.5286C48.2677 47.5286 48.289 47.5286 48.3104 47.529C53.4962 47.6343 57.7658 51.7697 58.0344 56.946C58.1785 59.6998 57.2141 62.3141 55.3191 64.3148C54.6536 65.0146 53.8915 65.6078 53.0652 66.0821C52.2769 66.5345 51.7069 67.3307 51.7069 68.2396C51.7069 69.458 50.7192 70.4457 49.5008 70.4457H48.1089ZM48.1087 72.9604C48.1087 72.9603 48.1088 72.9602 48.1089 72.9602C50.066 72.9607 51.6521 74.5473 51.6521 76.5044C51.6521 78.4405 50.1001 80.0139 48.1724 80.0482L48.1089 80.0488C48.1088 80.0488 48.1087 80.0487 48.1087 80.0486C48.1087 80.0485 48.1085 80.0484 48.1084 80.0485L48.108 80.0489C48.108 80.0489 48.1079 80.049 48.1078 80.049C46.1503 80.0489 44.5635 78.4619 44.5635 76.5046C44.5635 74.5471 46.1501 72.9603 48.1078 72.9602C48.1079 72.9602 48.108 72.9602 48.108 72.9603L48.1083 72.9606C48.1084 72.9607 48.1087 72.9606 48.1087 72.9604Z"
              fill="white"
            />
          </svg>
        </div>
      </div>

      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-[26px] font-bold text-text-primary mb-2">
          Forgot your password?
        </h1>
        <p className="text-sm text-text-secondary leading-relaxed">
          Please enter the email address associated with your account and we&apos;ll email you a
          link to reset your password.
        </p>
      </div>


      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Email Field */}
        <AuthFloatingInput
          label="Email address"
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError('');
          }}
          disabled={loading}
          error={error}
          autoFocus
        />

        {/* Error Message — above submit button */}
        {error && (
          <div className="p-3.5 bg-error-50 dark:bg-error-900/10 text-error-600 dark:text-error-400 rounded-lg text-sm">
            <p>{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full h-12 rounded-full bg-primary-500 font-bold text-[15px] hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Sending request...' : 'Send request'}
        </button>
      </form>

      {/* Back to Login */}
      <div className="text-center mt-6">
        <Link
          to="/auth/login"
          className="text-sm text-text-secondary hover:text-text-primary transition-colors inline-flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Return to sign in
        </Link>
      </div>
      </motion.div>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
